import { sleep } from '@enuygun/frontend-components'
import dayjs from 'dayjs'

import { savePreviousCategoryBeforeSubmit } from 'utils/searchForm'
import { shouldAppendIsNewRtParam } from 'utils/xExperiments'

import { getRooms } from './hotel'
import { logHomepageSearchFormSubmit, resolveHomepageSearchRef } from './searchFormSubmitTracking'
import type { SearchFormSubmitOptions } from './submitOptions'

const getHotelId = async (cityCode: string) => {
  try {
    const res = await fetch(`https://www.enuygun.com/ucak-bileti/get-hotel-slug/?cityCode=${cityCode}`)
    const data = await res.json()
    const hotelShowCheck = data?.default_check && data?.hotel_slug
    return {
      hotelSlug: data?.hotel_slug,
      hotelShowCheck,
      type: typeof data?.type === 'string' && data.type ? data.type : undefined,
    }
  } catch (error) {
    console.log('getHotelId', error)
    return { hotelSlug: null, hotelShowCheck: null, type: null }
  }
}

const firtsSlug = {
  default: 'flights/search',
  tr: 'ucak-bileti/arama',
  de: 'flug/suchen',
  es: 'vuelos/buscar',
  ar: 'flights/search',
  it: 'flights/search',
  fr: 'flights/search',
  pt: 'flights/search',
  ro: 'flights/search',
  pl: 'flights/search',
  lt: 'flights/search',
  uz: 'flights/search',
  sr: 'flights/search',
  ky: 'flights/search',
  tg: 'flights/search',
  kk: 'flights/search',
  nl: 'flights/search',
  en: 'flights/search',
  zh: 'flights/search',
  ka: 'flights/search',
  hi: 'flights/search',
  ja: 'flights/search',
  ko: 'flights/search',
  ur: 'flights/search',
  ta: 'flights/search',
  th: 'flights/search',
}

const airpotsTO = {
  default: 'to',
  tr: '',
  de: 'nach',
  es: 'a',
  ar: 'to',
  it: 'to',
  fr: 'to',
  pt: 'to',
  ro: 'to',
  pl: 'to',
  lt: 'to',
  uz: 'to',
  sr: 'to',
  ky: 'to',
  tg: 'to',
  kk: 'to',
  nl: 'to',
  en: 'to',
  zh: 'to',
  ka: 'to',
  hi: 'to',
  ja: 'to',
  ko: 'to',
  ur: 'to',
  ta: 'to',
  th: 'to',
}
const countrySlug = {
  default: 'flights',
  tr: 'ucak-bileti',
  de: 'flug',
  es: 'vuelos',
  ar: 'flights',
  it: 'flights',
  fr: 'flights',
  pt: 'flights',
  ro: 'flights',
  pl: 'flights',
  lt: 'flights',
  uz: 'flights',
  sr: 'flights',
  ky: 'flights',
  tg: 'flights',
  kk: 'flights',
  nl: 'flights',
  en: 'flights',
  zh: 'flights',
  ka: 'flights',
  hi: 'flights',
  ja: 'flights',
  ko: 'flights',
  ur: 'flights',
  ta: 'flights',
  th: 'flights',
}

const GEO_TRIP = {
  default: 'international',
  TR: 'domestic',
  CY: 'domestic',
}

const isTrOrCyCountry = (countryCode?: string) => {
  const code = countryCode?.toUpperCase()
  return code === 'TR' || code === 'CY'
}

const stripEmptyParams = (params: URLSearchParams) => {
  Array.from(params.keys()).forEach((key) => {
    const value = params.get(key)
    if (!value) params.delete(key)
  })
}

const stripEmptyRecordParams = (allParams: Record<string, unknown>) => {
  Object.keys(allParams).forEach((key) => {
    if (allParams[key] == undefined || allParams[key] == null || allParams[key] == '' || allParams[key] == '0') {
      delete allParams[key]
    }
  })
  return allParams
}

const detectSafariShowListHotel = (flightType: string, form) => {
  let showListHotel = flightType === 'multi-destination' ? form?.showListHotelMulti : form?.showListHotel

  if (globalThis.navigator !== undefined) {
    const isSafari = /^((?!chrome|android).)*safari/i.test(globalThis.navigator.userAgent)
    if (isSafari) {
      showListHotel = false
    }
  }

  return showListHotel
}

const resolveIsInternational = (form, flightType: string): boolean => {
  if (flightType === 'multi-destination') {
    return form?.multi?.some((leg) => {
      // Aynı ülke içindeki uçuşları iç hat olarak işaretle
      if (leg?.origin?.country_code && leg?.destination?.country_code && leg?.origin?.country_code === leg?.destination?.country_code) {
        return false
      }
      return true // Farklı ülkeler arası uçuşlar her zaman international
    })
  }

  // Aynı ülke içindeki uçuşları iç hat olarak işaretle
  if (form?.origin?.country_code && form?.destination?.country_code && form?.origin?.country_code === form?.destination?.country_code) {
    return false
  }
  return true // Farklı ülkeler arası uçuşlar her zaman international
}

const resolveTripValue = (form, flightType: string): string => {
  if (flightType === 'multi-destination') {
    return form?.multi?.every(
      (leg) => isTrOrCyCountry(leg?.origin?.country_code) && isTrOrCyCountry(leg?.destination?.country_code)
    )
      ? 'domestic'
      : 'international'
  }

  return isTrOrCyCountry(form?.origin?.country_code) && isTrOrCyCountry(form?.destination?.country_code) ? 'domestic' : 'international'
}

const resolveGeoTrip = (isInternational: boolean) => (isInternational ? 'international' : 'domestic')

const buildFlightSearchParams = ({
  form,
  isGlobal,
  refValue,
  newRtExperimentVariant,
}: {
  form
  isGlobal: boolean
  refValue: string
  newRtExperimentVariant?: string
}) => {
  const flightType = form?.flightTrip
  const gidis = form?.departureDate && dayjs(form?.departureDate).format('DD.MM.YYYY')
  const donus = form?.returnDate && dayjs(form?.returnDate).format('DD.MM.YYYY')
  const yetiskin = form?.guest?.adult
  const cocuk = form?.guest?.child
  const bebek = form?.guest?.infant
  const sinif = form?.cabinClass
  const save = '1'
  const transitFilter = form.transitFilter

  const gidisKey = isGlobal ? 'departure' : 'gidis'
  const donusKey = isGlobal ? 'return' : 'donus'
  const yetiskinKey = isGlobal ? 'adult' : 'yetiskin'
  const cocukKey = isGlobal ? 'child' : 'cocuk'
  const bebekKey = isGlobal ? 'infant' : 'bebek'
  const sinifKey = isGlobal ? 'class' : 'sinif'
  const aktarmasizKey = isGlobal ? 'direct' : 'aktarmasiz'

  const allParams = stripEmptyRecordParams({
    [gidisKey]: gidis,
    [donusKey]: donus,
    [yetiskinKey]: yetiskin,
    [cocukKey]: cocuk,
    [bebekKey]: bebek,
    [sinifKey]: sinif,
    save,
    [aktarmasizKey]: transitFilter ? '1' : '0',
    ref: refValue,
  })

  const params = new URLSearchParams(allParams as Record<string, string>)

  // Wingie özelinde direct seçildiğinde stop_counts=0 parametresini ekle
  if (isGlobal && transitFilter) {
    params.append('stop_counts', '0')
  }

  const isInternational = resolveIsInternational(form, flightType)
  const geoTrip = resolveGeoTrip(isInternational)
  const tripValue = resolveTripValue(form, flightType)

  params.append('geotrip', geoTrip)
  params.append('trip', tripValue)

  if (
    shouldAppendIsNewRtParam({
      isWingie: isGlobal,
      flightType,
      tripValue,
      geoTrip,
      experimentVariant: newRtExperimentVariant,
    })
  ) {
    params.append('is_new_rt', '1')
  }

  return { params, gidisKey, donusKey, yetiskin, cocuk, bebek, isInternational }
}

const buildFirstParams = ({
  form,
  flightType,
  airpotsTo,
  params,
  gidisKey,
  donusKey,
}: {
  form
  flightType: string
  airpotsTo: string
  params: URLSearchParams
  gidisKey: string
  donusKey: string
}) => {
  const origin = form?.origin?.slug
  const destination = form?.destination?.slug
  const airpotsID = `${form?.origin?.id.toLowerCase()}-${form?.destination?.id.toLowerCase()}`

  if (flightType === 'round-trip' || flightType === 'one-way') {
    return `${origin}${airpotsTo}-${destination}-${airpotsID}/?${params}`
  }

  if (flightType === 'multi-destination') {
    const multiInfo = form?.multi?.map((leg) => ({
      airIds: `${leg?.origin?.id}-${leg?.destination?.id}`,
      departureDay: dayjs(leg?.departureDate).format('DD.MM.YYYY'),
    }))

    params.delete(gidisKey)
    params.delete(donusKey)
    params.append('route_type', 'multi')

    const multiAirIds = multiInfo
      .map((info) => info?.airIds)
      .join('-')
      .toLowerCase()
    const multiDays = multiInfo?.map((info) => info?.departureDay).join('-')

    return `${multiAirIds}/${multiDays}/?${params}`
  }

  return undefined
}

const computeHotelCheckOutDate = (departureDate, returnDate) => {
  if (!returnDate || dayjs(departureDate).isSame(returnDate, 'day')) {
    return dayjs(departureDate).add(2, 'day').format('DD.MM.YYYY')
  }

  return dayjs(returnDate).format('DD.MM.YYYY')
}

const resolveHotelRefValue = (variant: string | undefined, hotelShowCheck: boolean) => {
  if (variant === 'B') {
    return hotelShowCheck ? 'homepage-ft-default_b' : 'homepage-ft_b'
  }

  return hotelShowCheck ? 'homepage-ft-default' : 'homepage-ft'
}

const handleHotelListRedirect = async ({
  form,
  flightType,
  BASE_PATH,
  firstSlug,
  firstParams,
  yetiskin,
  cocuk,
  bebek,
  variant,
}: {
  form
  flightType: string
  BASE_PATH: string
  firstSlug: string
  firstParams?: string
  yetiskin
  cocuk
  bebek
  variant?: string
}) => {
  const city_code = flightType === 'multi-destination' ? form?.multi[0]?.destination?.city_code : form?.destination?.city_code
  const { hotelSlug, hotelShowCheck, type: hotelListingType } = await getHotelId(city_code)
  const childCount = cocuk || 0
  const infantCount = bebek || 0
  const checkInDate = flightType === 'multi-destination' ? dayjs(form?.multi[0]?.departureDate).format('DD.MM.YYYY') : dayjs(form?.departureDate).format('DD.MM.YYYY')
  const departureDate = flightType === 'multi-destination' ? form.multi[0].departureDate : form?.departureDate
  const returnDate = flightType === 'multi-destination' ? form.multi[1].returnDate : form?.returnDate
  const checkOutDate = computeHotelCheckOutDate(departureDate, returnDate)

  const defaultChildAge = '8'
  const defaultInfantAge = '1'
  const occupancyStr = getRooms([
    {
      adults: yetiskin || 1,
      children: [...new Array(childCount).fill(defaultChildAge), ...new Array(infantCount).fill(defaultInfantAge)],
    },
  ])
  const hotelRefValue = resolveHotelRefValue(variant, hotelShowCheck)
  const hotelCountry = flightType === 'multi-destination' ? form.multi[0].destination.country_code : form.destination.country_code
  const hotelPathSegment = hotelListingType || 'bolge'
  const isHotelDetail = hotelPathSegment === 'detay'

  const hotelParams = new URLSearchParams({
    checkInDate,
    checkOutDate,
    ref: hotelRefValue,
    country: hotelCountry,
  })

  if (isHotelDetail) {
    hotelParams.append('rooms', occupancyStr)
    hotelParams.append('autoRequest', '1')
    hotelParams.append('p', 'hotel_detail')
    hotelParams.append('is_single_search', '1')
  } else {
    hotelParams.append('roomDetail', occupancyStr)
    hotelParams.append('p', 'search')
  }

  if (hotelSlug) {
    const hotelRedirect = `https://www.enuygun.com/otel/${hotelPathSegment}/${hotelSlug}/?${hotelParams}`
    //@ts-ignore
    globalThis.open(`${BASE_PATH}/${firstSlug}/${firstParams}`, '_blank')
    globalThis.location.href = hotelRedirect
  } else {
    //@ts-ignore
    globalThis.location.href = `${BASE_PATH}/${firstSlug}/${firstParams}`
  }
}

const handleVisaFreeDestinationSubmit = async ({
  form,
  market,
  language,
  url,
  isGlobal,
  delay,
  resolve,
}: {
  form
  market: string
  language: string
  url: string
  isGlobal: boolean
  delay: number
  resolve: () => void
}) => {
  const isVisaFreeDestination = form?.destination?.id === 'VF' || form?.destination?.city_code === 'VF'
  if (!isVisaFreeDestination || market !== 'tr') return false

  // Special URL format for visa-free countries: /ucak-bileti/istanbul-ista/vizesiz-ulkeler/
  const originSlug = form?.origin?.slug
  const originId = form?.origin?.id?.toLowerCase()
  const origin = `${originSlug}-${originId}`

  // Vizesiz ülkeler için tarih parametreleri ile URL oluştur
  const visaFreeFirstSlug = market === 'tr' ? 'ucak-bileti' : firtsSlug[language] || firtsSlug[market] || firtsSlug.default
  const baseVisaFreeUrl = `${url}/${visaFreeFirstSlug}/${origin}/vizesiz-ulkeler/`

  // Tüm parametreleri ekle
  const params = new URLSearchParams()

  // Tarih parametreleri
  if (form?.departureDate) {
    params.append('departure_date', dayjs(form.departureDate).format('YYYY-MM-DD'))
  }
  if (form?.returnDate) {
    params.append('return_date', dayjs(form.returnDate).format('YYYY-MM-DD'))
  }

  // Origin code
  if (originId) {
    params.append('origin_code', originId)
  }

  // Yolcu sayıları - hepsini ekle
  params.append('adult', (form?.guest?.adult || 1).toString())
  params.append('child', (form?.guest?.child || 0).toString())
  params.append('infant', (form?.guest?.infant || 0).toString())

  // Kabin sınıfı - her zaman ekle
  const cabinClass = form?.cabinClass === 'ekonomi' || !form?.cabinClass ? 'economy' : form.cabinClass
  params.append('cabin_class', cabinClass)

  // Direkt uçuş - her zaman ekle
  params.append('is_direct', form?.transitFilter ? 'true' : 'false')

  // Wingie özelinde direct seçildiğinde stop_counts=0 parametresini ekle
  if (isGlobal && form?.transitFilter) {
    params.append('stop_counts', '0')
  }

  // Redis parametresi
  params.append('redis', 'false')

  const visaFreeUrl = `${baseVisaFreeUrl}?${params.toString()}`

  globalThis.location.href = visaFreeUrl
  await sleep(delay)
  resolve()
  return true
}

const handleCountrySearchSubmit = async ({
  form,
  flightType,
  language,
  citiesKey,
  isGlobal,
  refValue,
  delay,
  resolve,
}: {
  form
  flightType: string
  language: string
  citiesKey: string
  isGlobal: boolean
  refValue: string
  delay: number
  resolve: () => void
}) => {
  // Çoklu uçuş (multi-destination) için ülke bazlı aramayı atla
  if (form?.destination?.is_country !== true || flightType === 'multi-destination') return false

  // Ülke bazlı arama için özel URL oluştur
  const originCode = form?.origin?.id?.toLowerCase()
  const destinationCode = form?.destination?.country_code?.toLowerCase()

  // Özel parametreler
  const countrySearchParams = new URLSearchParams({
    departure_date: dayjs(form?.departureDate).format('YYYY-MM-DD'),
    return_date: form?.returnDate ? dayjs(form?.returnDate).format('YYYY-MM-DD') : '',
    origin_code: originCode,
    destination_code: destinationCode,
    adult: form?.guest?.adult.toString(),
    child: form?.guest?.child.toString(),
    infant: form?.guest?.infant.toString(),
    cabin_class: form?.cabinClass === 'ekonomi' ? 'economy' : form?.cabinClass,
    is_direct: form.transitFilter ? 'true' : 'false',
    redis: 'false',
    ref: refValue,
  })

  // Wingie özelinde direct seçildiğinde stop_counts=0 parametresini ekle
  if (isGlobal && form.transitFilter) {
    countrySearchParams.append('stop_counts', '0')
  }

  // Boş değerleri temizle
  stripEmptyParams(countrySearchParams)

  // Ülke bazlı URL oluştur - pathLocale kullanma çünkü ülke araması için locale gerekmiyor
  const countryUrl = `/${language === 'en' ? countrySlug.default : countrySlug[language] || countrySlug.default}/${form?.origin?.slug}-${
    form?.destination?.slug
  }-${originCode}-${destinationCode}/${citiesKey}/?${countrySearchParams}`

  //@ts-ignore
  globalThis.location.href = countryUrl
  await sleep(delay)
  resolve()
  return true
}

export const flightHandleSubmit = async (form, options: SearchFormSubmitOptions = {}) => {
  const {
    market,
    index = null,
    delay = 0,
    url = 'https://www.enuygun.com',
    language,
    variant,
    productCategory,
    newRtExperimentVariant,
  } = options
  const resolvedMarket = market ?? 'tr'
  const refValue = resolveHomepageSearchRef(variant)
  const noopResolve = () => undefined

  if (index !== null) savePreviousCategoryBeforeSubmit(String(index))
  logHomepageSearchFormSubmit({ variant, productCategory: productCategory || 'ucak-bileti' })

  const isGlobal = resolvedMarket !== 'tr'
  const citiesKey = isGlobal ? 'cities' : 'sehirler'
  const flightType = form?.flightTrip

  if (
    await handleVisaFreeDestinationSubmit({
      form,
      market: resolvedMarket,
      language: language ?? '',
      url,
      isGlobal,
      delay,
      resolve: noopResolve,
    })
  ) {
    return
  }

  if (
    await handleCountrySearchSubmit({
      form,
      flightType,
      language: language ?? '',
      citiesKey,
      isGlobal,
      refValue,
      delay,
      resolve: noopResolve,
    })
  ) {
    return
  }

  const BASE_PATH = url
  // Language öncelikli fallback logic - tanımlanmamış diller için default kullanılır
  const languageSlug = language === undefined ? undefined : firtsSlug[language]
  const languageAirportTo = language === undefined ? undefined : airpotsTO[language]
  const firstSlug = languageSlug || firtsSlug[resolvedMarket] || firtsSlug.default
  const airpotsTo = isGlobal ? `-${languageAirportTo || airpotsTO[resolvedMarket] || airpotsTO.default}` : ''
  const showListHotel = detectSafariShowListHotel(flightType, form)

  const { params, gidisKey, donusKey, yetiskin, cocuk, bebek } = buildFlightSearchParams({
    form,
    isGlobal,
    refValue,
    newRtExperimentVariant,
  })

  const firstParams = buildFirstParams({
    form,
    flightType,
    airpotsTo,
    params,
    gidisKey,
    donusKey,
  })

  if (showListHotel) {
    await handleHotelListRedirect({
      form,
      flightType,
      BASE_PATH,
      firstSlug,
      firstParams,
      yetiskin,
      cocuk,
      bebek,
      variant,
    })
  }

  await sleep(delay)

  if (!showListHotel) {
    //@ts-ignore
    globalThis.location.href = `${BASE_PATH}/${firstSlug}/${firstParams}`
  }
}
